/**
 * Weather — the guest-app's view of the accommodation's forecast.
 *
 * Fetches the normalized payload from the core-api (`GET /weather/:slug`, which
 * does the upstream calling and caching) and keeps a local copy in the guest's
 * browser on top of that. Two caches, two different jobs: the server's spares
 * the upstream provider, this one spares the *guest* — reopening the app should
 * paint a temperature immediately rather than after a round trip, and a guest
 * walking through the Sächsische Schweiz with no signal should still see the
 * forecast they loaded at breakfast.
 *
 * The API deliberately returns data, not wording: a condition enum plus
 * numbers. Turning that into a Lucide icon and German text is this class's
 * other half (see ICONS / LABELS) — which is also what keeps the endpoint
 * reusable by the client-admin later.
 */
import { _lcs, locale } from "../i18n";

export class Weather
{
    static STORAGE_KEY = "weather";

    // How long a cached forecast is served without revalidating. Matches the
    // server's own TTL — going lower just adds requests that return the
    // identical body.
    static TTL_MS = 30 * 60 * 1000;

    // A cached forecast older than this is not worth showing at all: beyond a
    // few hours "current conditions" stops being current.
    static MAX_AGE_MS = 6 * 60 * 60 * 1000;

    /**
     * Crediting the source is a condition of the data licence, so the sheet must
     * always have something to render — this stands in if a payload ever reaches
     * us without its own attribution block.
     */
    static FALLBACK_ATTRIBUTION = {
        text       : "MET Norway",
        url        : "https://api.met.no/",
        license    : "CC BY 4.0",
        licenseUrl : "https://creativecommons.org/licenses/by/4.0/"
    };

    /**
     * Condition enum -> icon. Entries with day/night variants get a sun or a
     * moon; the rest look the same at any hour (rain is rain).
     */
    static ICONS = {
        "clear"          : { day: "sun",           night: "moon" },
        "fair"           : { day: "cloud-sun",     night: "cloud-moon" },
        "partly-cloudy"  : { day: "cloud-sun",     night: "cloud-moon" },
        "cloudy"         : "cloudy",
        "fog"            : "cloud-fog",
        "rain"           : { light: "cloud-drizzle", heavy: "cloud-rain-wind", base: "cloud-rain" },
        "rain-showers"   : { day: "cloud-sun-rain", night: "cloud-moon-rain" },
        "sleet"          : "cloud-hail",
        "sleet-showers"  : "cloud-hail",
        "snow"           : "cloud-snow",
        "snow-showers"   : "cloud-snow"
    };

    /**
     * Condition enum -> label, by intensity where the distinction is one a guest
     * actually cares about ("Leichter Regen" vs "Starker Regen"). The German
     * wording IS the dictionary key — this is a class constant, evaluated
     * before I18n exists, so label() translates on the way out.
     */
    static LABELS = {
        "clear"         : { day: "Sonnig", night: "Klar" },
        "fair"          : "Heiter",
        "partly-cloudy" : "Teils bewölkt",
        "cloudy"        : "Bewölkt",
        "fog"           : "Nebel",
        "rain"          : { light: "Leichter Regen",       base: "Regen",              heavy: "Starker Regen" },
        "rain-showers"  : { light: "Leichte Schauer",      base: "Regenschauer",       heavy: "Starke Schauer" },
        "sleet"         : { light: "Leichter Schneeregen", base: "Schneeregen",        heavy: "Starker Schneeregen" },
        "sleet-showers" : { light: "Leichte Schneeregenschauer", base: "Schneeregenschauer", heavy: "Starke Schneeregenschauer" },
        "snow"          : { light: "Leichter Schneefall",  base: "Schneefall",         heavy: "Starker Schneefall" },
        "snow-showers"  : { light: "Leichte Schneeschauer", base: "Schneeschauer",     heavy: "Starke Schneeschauer" }
    };

    // 16-point compass, German source ("O" for Ost). Translated in compass():
    // every language names its own points — en "E", ru "В", cs "V".
    static COMPASS = [
        "N", "NNO", "NO", "ONO", "O", "OSO", "SO", "SSO",
        "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"
    ];

    constructor( app )
    {
        this.app = app;

        // Last known forecast (from cache or network) and when it was stored.
        this.data = null;
        this.storedAt = 0;

        // Set once a load has finished without producing anything usable, so the
        // UI can say so instead of spinning forever.
        this.failed = false;

        this._pending = null;

        this._restore();
    }

    // ------------------------------------------------------------------
    // Loading
    // ------------------------------------------------------------------

    /**
     * Whether a payload carries everything the screens read: the current
     * reading, the hourly window and the daily outlook. Guards both the network
     * response and the restored cache, so a partial or outdated shape is treated
     * as no data rather than rendering a view with a hole in it.
     */
    static isUsable( data )
    {
        return !!data && !!data.current
            && Array.isArray( data.hours )
            && Array.isArray( data.days );
    }

    /**
     * The forecast we can render right now, without waiting — the in-memory or
     * restored copy, as long as it is not absurdly old. Null when we have
     * nothing yet.
     */
    peek()
    {
        if ( this.data && ( Date.now() - this.storedAt ) < Weather.MAX_AGE_MS )
        {
            return this.data;
        }
        return null;
    }

    /** True once the cached copy is old enough to be worth refreshing. */
    isFresh()
    {
        return !!this.data && ( Date.now() - this.storedAt ) < Weather.TTL_MS;
    }

    /**
     * Resolve the forecast, fetching only when the cached copy is stale.
     * Concurrent callers share one request. Never throws — a failed load
     * resolves to the stale copy if there is one, else null.
     *
     * @param {boolean} [force=false] Refetch even when the cache is fresh.
     * @returns {Promise<Object|null>}
     */
    async load( force = false )
    {
        if ( !force && this.isFresh() )
        {
            return this.data;
        }

        if ( this._pending )
        {
            return await this._pending;
        }

        this._pending = this._fetch().finally( () => { this._pending = null; } );
        return await this._pending;
    }

    async _fetch()
    {
        const slug = this.app.theme.getTenantSlug();
        if ( !slug )
        {
            // No tenant resolved (bundled demo mode) — there is no location to
            // forecast for, so don't pretend otherwise. peek(), not this.data,
            // so an over-age reading is reported as unavailable here too.
            this.failed = !this.peek();
            return this.peek();
        }

        try
        {
            const { result } = await this.app.api.get( '/weather/' + encodeURIComponent( slug ) );
            if ( result && 200 === +result.status && Weather.isUsable( result.data ) )
            {
                this.data = result.data;
                this.storedAt = Date.now();
                this.failed = false;
                this._persist();
                return this.data;
            }
        }
        catch ( e )
        {
            console.error( e );
        }

        // Keep serving what we had; only report failure when we have nothing.
        this.failed = !this.peek();
        return this.peek();
    }

    // ------------------------------------------------------------------
    // Local persistence (survives app restarts / offline opens)
    // ------------------------------------------------------------------

    _persist()
    {
        try
        {
            this.app.storage.save( Weather.STORAGE_KEY, {
                slug     : this.app.theme.getTenantSlug(),
                storedAt : this.storedAt,
                data     : this.data
            } );
        }
        catch ( e )
        {
            // A full or unavailable localStorage must not break the screen.
            console.error( e );
        }
    }

    _restore()
    {
        let cached = null;
        try
        {
            cached = this.app.storage.load( Weather.STORAGE_KEY );
        }
        catch ( e )
        {
            console.error( e );
        }

        // Reject anything that does not carry every part the UI reads. Guests
        // keep these entries across app updates, so a payload cached by an older
        // build (one without hours[], say) would otherwise be served as "fresh"
        // and leave a view empty until the TTL lapsed. Failing the shape check
        // simply drops it and triggers an immediate refetch.
        if ( !Weather.isUsable( cached && cached.data ) )
        {
            return;
        }

        // Each tenant is its own subdomain (its own localStorage), but a slug
        // check keeps a stale entry from ever bleeding into another tenant.
        if ( cached.slug && cached.slug !== this.app.theme.getTenantSlug() )
        {
            return;
        }

        this.data = cached.data;
        this.storedAt = +cached.storedAt || 0;
    }

    // ------------------------------------------------------------------
    // Presentation helpers (static — the sheet component uses them too)
    // ------------------------------------------------------------------

    /** Icon modifier for a symbol, e.g. "cloud-sun" for `icon--cloud-sun`. */
    static icon( symbol )
    {
        if ( !symbol )
        {
            return "cloud";
        }
        if ( symbol.thunder )
        {
            return "cloud-lightning";
        }

        const entry = Weather.ICONS[ symbol.condition ];
        if ( !entry )
        {
            return "cloud";
        }
        if ( typeof entry === "string" )
        {
            return entry;
        }

        // Day/night pair, or an intensity set — whichever this condition uses.
        if ( entry.day || entry.night )
        {
            return ( false === symbol.isDay ? entry.night : entry.day ) || entry.day;
        }
        return Weather._byIntensity( entry, symbol.intensity );
    }

    /** Condition label in the current language, e.g. "Leichte Schauer & Gewitter". */
    static label( symbol )
    {
        if ( !symbol )
        {
            return _lcs( "Wetter" );
        }

        const entry = Weather.LABELS[ symbol.condition ];
        let label = "Wetter";

        if ( typeof entry === "string" )
        {
            label = entry;
        }
        else if ( entry && ( entry.day || entry.night ) )
        {
            label = ( false === symbol.isDay ? entry.night : entry.day ) || entry.day;
        }
        else if ( entry )
        {
            label = Weather._byIntensity( entry, symbol.intensity );
        }

        return symbol.thunder
            ? _lcs( "{label} & Gewitter", { label : _lcs( label ) } )
            : _lcs( label );
    }

    static _byIntensity( entry, intensity )
    {
        if ( "light" === intensity && entry.light ) return entry.light;
        if ( "heavy" === intensity && entry.heavy ) return entry.heavy;
        return entry.base;
    }

    /** Whole degrees with the degree sign, e.g. "21°". */
    static temp( value )
    {
        if ( null === value || undefined === value || !Number.isFinite( +value ) )
        {
            return "–";
        }
        return Math.round( +value ) + "°";
    }

    /** 16-point compass point for a "wind comes from" bearing. */
    static compass( degrees )
    {
        if ( null === degrees || undefined === degrees || !Number.isFinite( +degrees ) )
        {
            return null;
        }
        const index = Math.round( ( ( +degrees % 360 ) + 360 ) % 360 / 22.5 ) % 16;
        return _lcs( Weather.COMPASS[ index ] );
    }

    /** Decimal notation in the guest's locale, e.g. "0,5" / "0.5". */
    static number( value, decimals = 1 )
    {
        if ( null === value || undefined === value || !Number.isFinite( +value ) )
        {
            return null;
        }
        return ( +value ).toLocaleString( locale(), {
            minimumFractionDigits : decimals,
            maximumFractionDigits : decimals
        } );
    }

    /** "Heute" for today, otherwise the short weekday ("Mo", "Di", …). */
    static weekday( isoDate, isToday = false )
    {
        if ( isToday )
        {
            return _lcs( "Heute" );
        }
        const date = Weather._parseDate( isoDate );
        return date ? date.toLocaleDateString( locale(), { weekday : "short" } ) : "";
    }

    /**
     * How a date reads relative to another: "Heute", "Morgen", or the short
     * weekday. Used to label where the hourly rail crosses midnight.
     */
    static relativeDay( isoDate, fromIsoDate )
    {
        const date = Weather._parseDate( isoDate );
        const from = Weather._parseDate( fromIsoDate );

        if ( date && from )
        {
            // Rounded, so a DST change (a 23- or 25-hour day) still lands right.
            const days = Math.round( ( date - from ) / 86400000 );
            if ( 0 === days ) return _lcs( "Heute" );
            if ( 1 === days ) return _lcs( "Morgen" );
        }

        return Weather.weekday( isoDate );
    }

    /** "26.07." — the day/month a forecast row refers to. */
    static dayMonth( isoDate )
    {
        const date = Weather._parseDate( isoDate );
        if ( !date )
        {
            return "";
        }
        // Intl, not a hand-built "26.07." — en puts the month first.
        return date.toLocaleDateString( locale(), { day : "2-digit", month : "2-digit" } );
    }

    /**
     * Local wall-clock time of an instant, e.g. "10:00" — used for the
     * "Aktualisiert" line so the guest can tell how current the reading is.
     */
    static time( isoTimestamp )
    {
        if ( !isoTimestamp )
        {
            return null;
        }
        const date = new Date( isoTimestamp );
        if ( Number.isNaN( date.getTime() ) )
        {
            return null;
        }
        return String( date.getHours() ).padStart( 2, "0" ) + ":" +
               String( date.getMinutes() ).padStart( 2, "0" );
    }

    /**
     * Parse a plain "YYYY-MM-DD" as a *local* date. `new Date( "2026-07-26" )`
     * would read it as UTC midnight and shift the weekday west of Greenwich.
     */
    static _parseDate( isoDate )
    {
        const parts = String( isoDate || "" ).split( "-" );
        if ( 3 !== parts.length )
        {
            return null;
        }
        const date = new Date( +parts[ 0 ], +parts[ 1 ] - 1, +parts[ 2 ] );
        return Number.isNaN( date.getTime() ) ? null : date;
    }
}
